import React from "react";

/*
 * Beyond the Box — logo routes.
 * All marks are monochrome (currentColor), stroke-based, sharp, and legible
 * down to 16px. viewBox 0 0 32 32 so they double as favicons.
 */

/* ROUTE 1 — "The Opening": a precise frame with a break in the top edge,
   and the removed segment offset above/right — the piece that moved beyond. */
export function MarkOpening({ className = "", strokeWidth = 2 }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="square" className={className} aria-hidden="true">
      <path d="M13 9H4v19h24V9h-9" />
      <path d="M15 4h9" />
    </svg>
  );
}

/* ROUTE 2 — "Beyond the Line": a closed frame with one edge overshooting
   the boundary — a single controlled line extending past the box. */
export function MarkBeyond({ className = "", strokeWidth = 2 }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="square" className={className} aria-hidden="true">
      <path d="M5 9h26" />
      <path d="M5 9v18h19V9" />
    </svg>
  );
}

/* ROUTE 3 — "The Offset": nested corners where the inner frame has escaped
   the outer boundary — architectural, severe, very legible small. */
export function MarkOffset({ className = "", strokeWidth = 2 }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="square" className={className} aria-hidden="true">
      <path d="M4 12v16h16" />
      <path d="M12 4h16v16" />
    </svg>
  );
}

/* Wordmark — Cormorant, with an optional rule under "Box" that overshoots
   the text: the boundary being exceeded, expressed typographically. */
export function Wordmark({ className = "", overshoot = false }) {
  return (
    <span className={`font-display leading-none tracking-tight ${className}`}>
      Beyond the{" "}
      <span className="relative inline-block">
        Box
        {overshoot && (
          <span className="absolute -bottom-[0.14em] left-0 h-[2px] w-[128%] bg-accent" />
        )}
      </span>
    </span>
  );
}

/* ADOPTED MARK — "Beyond the Box": a solid front square with a second square
   peeking beyond it to the upper-right (only its top + right edges visible,
   the rest hidden behind the front square). Square viewBox → favicon-ready. */
export function MarkBtb({ className = "", strokeWidth = 2.25 }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="square"
      strokeLinejoin="miter"
      className={className}
      aria-hidden="true"
    >
      {/* back square — top edge + right edge only (the box moving beyond) */}
      <polyline points="12,2 30,2 30,20" />
      {/* front square — complete outline, sits on top */}
      <rect x="2" y="11" width="18" height="18" />
    </svg>
  );
}

/* Full lockup — offset mark + stacked BEYOND / THE / BOX wordmark rendered in
   the site heading face (Archivo). currentColor throughout for light/dark. */
export function LogoLockup({ className = "", iconClassName = "h-9 w-9" }) {
  return (
    <span className={`flex items-center gap-3 text-foreground ${className}`}>
      <MarkBtb className={`${iconClassName} shrink-0 transition-colors group-hover:text-accent`} />
      <span className="font-display text-[0.72rem] font-bold uppercase leading-[1.02] tracking-[0.06em]">
        <span className="block">Beyond</span>
        <span className="block">the</span>
        <span className="block">Box</span>
      </span>
    </span>
  );
}
